import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, MessageCircle, Loader2, Send, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, waLink } from "@/lib/site";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#0f0f0f] border border-white/15 focus:border-[#f95700] focus:outline-none text-white placeholder:text-neutral-600 h-14 px-4 text-base transition-colors";

export const Contact = () => {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { ...f, kind: "kontakt" });
      toast.success("Nachricht gespeichert – wir melden uns zeitnah.");
      setF({ name: "", phone: "", email: "", message: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Senden fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative w-full bg-white text-[#0a0a0a] py-20 sm:py-24 lg:py-32">
      <div className="w-full px-5 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow chapter="09" dark>
            Kontakt
          </Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display uppercase font-black leading-[0.9] tracking-tight text-[clamp(2.1rem,7.5vw,5rem)] mb-10 lg:mb-14">
            Sprechen Sie <span className="text-[#f95700]">mit uns</span>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-5 space-y-px bg-neutral-200">
            <Reveal className="bg-[#0a0a0a] text-white p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <MapPin size={22} className="text-[#f95700] shrink-0 mt-1" />
                <div>
                  <div className="eyebrow text-neutral-500 mb-2">Adresse</div>
                  <address className="not-italic font-display text-xl sm:text-2xl font-bold uppercase leading-tight">
                    {COMPANY.name}
                    <br />
                    {COMPANY.street}
                    <br />
                    {COMPANY.zip} {COMPANY.city}
                    <br />
                    <span className="text-neutral-400">{COMPANY.country}</span>
                  </address>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.05} className="bg-white p-6 sm:p-8 grid sm:grid-cols-2 gap-6">
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone-link" className="group">
                <div className="eyebrow text-neutral-500 mb-2 flex items-center gap-2">
                  <Phone size={13} className="text-[#f95700]" /> Telefon
                </div>
                <div className="font-display text-xl font-bold group-hover:text-[#f95700] transition-colors">
                  {COMPANY.phoneDisplay}
                </div>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="group">
                <div className="eyebrow text-neutral-500 mb-2 flex items-center gap-2">
                  <Mail size={13} className="text-[#f95700]" /> E-Mail
                </div>
                <div className="font-mono-t text-sm break-all group-hover:text-[#f95700] transition-colors">
                  {COMPANY.email}
                </div>
              </a>
            </Reveal>

            <Reveal delay={0.1} className="bg-white p-6 sm:p-8">
              <div className="eyebrow text-neutral-500 mb-4 flex items-center gap-2">
                <Clock size={13} className="text-[#f95700]" /> Öffnungszeiten
              </div>
              <ul className="space-y-2">
                {COMPANY.hours.map((h) => (
                  <li key={h.d} className="flex justify-between gap-4 border-b border-neutral-200 pb-2">
                    <span className="text-[13px] sm:text-sm font-semibold">{h.d}</span>
                    <span className="font-mono-t text-[12px] sm:text-sm text-neutral-600 whitespace-nowrap">{h.t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.15} className="bg-white p-6 sm:p-8 space-y-3">
              <a
                href={waLink("Hallo Global Move Europa, ich habe eine Frage zu einem Transport.")}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-button"
                className="w-full inline-flex items-center justify-between gap-4 bg-[#25D366] hover:bg-[#1eb857] text-white h-16 px-6 sm:px-7 font-display text-base sm:text-lg font-bold uppercase tracking-wide transition-colors"
              >
                Frage per WhatsApp
                <MessageCircle size={22} />
              </a>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="contact-call-button"
                className="w-full inline-flex items-center justify-between gap-4 bg-[#0a0a0a] hover:bg-[#f95700] text-white h-16 px-6 sm:px-7 font-display text-base sm:text-lg font-bold uppercase tracking-wide transition-colors"
              >
                Jetzt anrufen
                <Phone size={20} />
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-8">
            <Reveal className="bg-[#0a0a0a] p-6 sm:p-8 lg:p-10">
              <h3 className="font-display text-2xl sm:text-3xl font-black uppercase text-white mb-6">Anfrageformular</h3>
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required data-testid="contact-input-name" className={inputCls} placeholder="Name *" value={f.name} onChange={(e) => set("name", e.target.value)} />
                  <input required data-testid="contact-input-phone" className={inputCls} placeholder="Telefon *" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <input data-testid="contact-input-email" className={inputCls} placeholder="E-Mail" value={f.email} onChange={(e) => set("email", e.target.value)} />
                <textarea
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  className={`${inputCls} h-auto py-3 resize-none`}
                  placeholder="Ihre Nachricht *"
                  value={f.message}
                  onChange={(e) => set("message", e.target.value)}
                />
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={f.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                    data-testid="contact-consent-checkbox"
                    className="mt-1 w-5 h-5 accent-[#f95700] shrink-0"
                  />
                  <span className="text-[13px] text-neutral-400 leading-relaxed">
                    Ich stimme zu, dass meine Angaben zur Bearbeitung der Anfrage gespeichert und
                    verarbeitet werden.{" "}
                    <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#f95700] underline">
                      Datenschutzerklärung
                    </a>
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-3 bg-[#f95700] hover:bg-[#e04e00] disabled:opacity-50 text-white h-16 px-7 font-display text-base sm:text-lg font-bold uppercase tracking-wide transition-colors"
                >
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  Nachricht senden
                </button>
              </form>
            </Reveal>

            <Reveal delay={0.05} className="relative border border-neutral-200">
              {showMap ? (
                <iframe
                  title="Standort Global Move Europa"
                  data-testid="google-map-iframe"
                  src="https://www.google.com/maps?q=Gro%C3%9Fe%20Weidestra%C3%9Fe%202%2C%2028195%20Bremen&output=embed"
                  className="w-full h-[300px] sm:h-[360px] grayscale-[35%]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  data-testid="map-consent-button"
                  className="w-full h-[300px] sm:h-[360px] bg-neutral-100 flex flex-col items-center justify-center gap-4 px-6 text-center hover:bg-neutral-200 transition-colors"
                >
                  <MapIcon size={32} className="text-[#f95700]" />
                  <span className="font-display text-xl sm:text-2xl font-bold uppercase">Karte laden</span>
                  <span className="text-[13px] text-neutral-600 max-w-md leading-relaxed">
                    Aus Datenschutzgründen wird Google Maps erst nach Ihrer Zustimmung geladen. Dabei
                    werden Daten an Google übertragen.
                  </span>
                </button>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
